import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Edit2, Package, Shield, Star } from 'lucide-react'
import type { ArtefactTemplate } from '../../../types/artefact-templates'
import { ArtefactTagList } from '../../velociraptor/ArtefactTagList'

export type ScopeIcon = 'system' | 'org' | 'case'

export function TemplateStepLoading(): ReactElement {
  return (
    <div className="flex items-center justify-center p-8" role="status">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-fuchsia-600 border-t-transparent" />
    </div>
  )
}

interface TemplateStepEmptyProps {
  noTemplatesLabel: string
}

export function TemplateStepEmpty({ noTemplatesLabel }: TemplateStepEmptyProps): ReactElement {
  return (
    <div className="py-8 text-center text-slate-500 dark:text-slate-400">
      <Package className="mx-auto mb-3 h-10 w-10 text-slate-300 dark:text-slate-600" />
      <p className="text-sm">{noTemplatesLabel}</p>
    </div>
  )
}

interface BadgesProps {
  isDefault: boolean
  scopeIcon: ScopeIcon
  t: TFunction<'cases'>
}

function TemplateBadges({ isDefault, scopeIcon, t }: BadgesProps): ReactElement {
  return (
    <>
      {isDefault && (
        <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-100 px-2 py-0.5 text-xs font-medium text-fuchsia-700 dark:bg-fuchsia-900/50 dark:text-fuchsia-300">
          <Star className="h-3 w-3" />
          {t('collections.artefactTemplates.defaultBadge')}
        </span>
      )}
      {scopeIcon === 'system' && (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          <Shield className="h-3 w-3" />
          {t('collections.artefactTemplates.scopeSystem')}
        </span>
      )}
    </>
  )
}

interface CardProps {
  template: ArtefactTemplate
  isSelected: boolean
  scopeIcon: ScopeIcon
  onSelect: (id: string) => void
  onEdit: (template: ArtefactTemplate) => void
  t: TFunction<'cases'>
}

export function TemplateCard({
  template,
  isSelected,
  scopeIcon,
  onSelect,
  onEdit,
  t,
}: CardProps): ReactElement {
  const containerClass = isSelected
    ? 'border-fuchsia-500 dark:border-fuchsia-600 bg-fuchsia-50 dark:bg-fuchsia-950/30'
    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-fuchsia-300 dark:hover:border-fuchsia-700'
  const indicatorClass = isSelected
    ? 'border-fuchsia-600 dark:border-fuchsia-400'
    : 'border-slate-300 dark:border-slate-600'
  return (
    <div
      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 shadow-sm transition ${containerClass}`}
      onClick={() => {
        onSelect(template.id)
      }}
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(template.id)
        }
      }}
    >
      <div
        className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${indicatorClass}`}
      >
        {isSelected && <div className="h-2 w-2 rounded-full bg-fuchsia-600 dark:bg-fuchsia-400" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-heading truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
            {template.name}
          </span>
          <TemplateBadges isDefault={template.isDefault} scopeIcon={scopeIcon} t={t} />
        </div>
        {template.description && (
          <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">
            {template.description}
          </p>
        )}
        <div className="mt-2 flex items-center gap-4 text-xs text-slate-400 dark:text-slate-500">
          <span>
            {t('collections.artefactTemplates.artefactCount', { count: template.artefactCount })}
          </span>
          {template.supportedPlatforms.length > 0 && (
            <span>{template.supportedPlatforms.join(', ')}</span>
          )}
        </div>
        <ArtefactTagList artefacts={template.artefacts} templateName={template.name} />
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onEdit(template)
        }}
        className="flex-shrink-0 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        aria-label={t('collections.artefactTemplates.editTemplate')}
      >
        <Edit2 className="h-4 w-4" />
      </button>
    </div>
  )
}

interface GroupProps {
  label: string
  templates: ArtefactTemplate[]
  selectedId: string | null
  scopeIcon: ScopeIcon
  onSelect: (id: string) => void
  onEdit: (template: ArtefactTemplate) => void
  t: TFunction<'cases'>
}

export function TemplateGroup({
  label,
  templates,
  selectedId,
  scopeIcon,
  onSelect,
  onEdit,
  t,
}: GroupProps): ReactElement | null {
  if (templates.length === 0) return null
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {label}
      </h4>
      {templates.map((tpl) => (
        <TemplateCard
          key={tpl.id}
          template={tpl}
          isSelected={selectedId === tpl.id}
          scopeIcon={scopeIcon}
          onSelect={onSelect}
          onEdit={onEdit}
          t={t}
        />
      ))}
    </div>
  )
}
